import React from 'react'
import { Grid } from '@fs/zion-ui'
import OneMergePageDecorator from '../../merge-common/OneMergePageDecorator'
import OneMergePageFirstSeparator from './components/OneMergePageFirstSeparator'
import RoundedCard from '../../merge-common/layout/RoundedCard'
import InBetweenBorderGrid from '../../merge-common/layout/InBetweenBorderGrid'
import InBetweenSeparatorGrid from '../../merge-common/layout/InBetweenSeparatorGrid'
import MergeGrid from '../../merge-common/MergeGrid'
import MergeGridProvider from '../../merge-common/providers/MergeGridProvider'
import HeightProvider from '../../merge-common/providers/HeightProvider'
import MergeLayoutProvider, { useMergeIsMobile } from '../../merge-common/providers/MergeLayoutProvider'
import OneMergePageArrow from '../../merge-common/OneMergePageArrow'
import { MERGE_DIFF_PX_AMOUNT } from '../../merge-common/helpers'
import MergeHeaderHeadingSkeleton from './components/header/MergeHeaderHeadingSkeleton'
import MergeActionButtonRowSkeleton from './components/MergeActionButtonRowSkeleton'
import PersonColumnHeadingSkeleton from './components/PersonColumnHeadingSkeleton'
import MergeColumnSkeleton from './components/MergeColumnSkeleton'
import VitalsSectionSkeleton from './components/new-sections/vitals/VitalsSectionSkeleton'
import ResultantCardSkeleton from './components/ResultantCardSkeleton'

/**
 * Skeleton for the merge page. It is intentionally data-provider-free (aside from its own layout-only
 * MergeLayoutProvider, MergeGridProvider, and HeightProvider) and dependency-light so it can be eagerly bundled and
 * used as the route fallback in App as well as the data-loading placeholder in
 * OneMergePageProvider. It mirrors the merge comparison: the Duplicate Person / Main Person columns and, on wider
 * screens, the Final Result column — matching the real page, which only shows the resultant column outside of
 * mobile sizes.
 */
export function OneMergePageSkeleton(): JSX.Element {
  return (
    <MergeLayoutProvider>
      <OneMergePageSkeletonContent />
    </MergeLayoutProvider>
  )
}

function OneMergePageSkeletonContent(): JSX.Element {
  const isMobile = useMergeIsMobile()
  const showThirdColumn = !isMobile

  return (
    <div data-testid="one-merge-page-skeleton">
      <OneMergePageFirstSeparator />
      <OneMergePageDecorator>
        <MergeGridProvider showThirdColumn={showThirdColumn}>
          <HeightProvider>
            <Grid guttersY="xs">
              <MergeHeaderHeadingSkeleton />
              {/* eslint-disable-next-line test-selectors/button -- NA */}
              <MergeActionButtonRowSkeleton />
              <MergeGrid>
                <InBetweenSeparatorGrid columnCount={2}>
                  <PersonColumnHeadingSkeleton personType="duplicate" />
                  <PersonColumnHeadingSkeleton personType="survivor" />
                </InBetweenSeparatorGrid>
                <PersonColumnHeadingSkeleton personType="resultant" />
              </MergeGrid>
              <MergeGrid arrowDiv={<OneMergePageArrow style={{ marginInlineEnd: MERGE_DIFF_PX_AMOUNT }} />}>
                <RoundedCard edgeToEdge={isMobile}>
                  <InBetweenBorderGrid>
                    <InBetweenBorderGrid columnCount={2}>
                      <MergeColumnSkeleton personType="duplicate" />
                      <MergeColumnSkeleton personType="survivor" />
                    </InBetweenBorderGrid>
                    <VitalsSectionSkeleton />
                  </InBetweenBorderGrid>
                </RoundedCard>
                <ResultantCardSkeleton />
              </MergeGrid>
            </Grid>
          </HeightProvider>
        </MergeGridProvider>
      </OneMergePageDecorator>
    </div>
  )
}
